import type { ApolloClient } from 'apollo-client'
import type {
  ListBoardMessageAppearancesForArrayHistory,
  ListBoardMessageAppearancesForArrayHistoryVariables
} from '../__generated__/ListBoardMessageAppearancesForArrayHistory'
import { lowerBoundDescByCreated } from './matching'
import { LIST_BOARD_MESSAGE_APPEARANCES_QUERY } from './queries'
import type { MessageAppearanceRow, TimeRange } from './types'

const DEFAULT_PAGE_SIZE = 50

export type EnsureRangeStatus = 'complete' | 'partial' | 'exhausted'

const expandRanges = (ranges: TimeRange[], bufferMs: number) =>
  ranges.map(range => ({
    min: range.min - bufferMs,
    max: range.max + bufferMs
  }))

export class AppearanceHistoryStore {
  private readonly boardId: string
  private readonly client: ApolloClient<object>
  private readonly pageSize: number
  private appearances: MessageAppearanceRow[] = []
  private nextCursor: string | null = null
  private exhausted = false
  private hasFetchedHead = false
  private loading = false
  private error: string | null = null

  constructor({
    client,
    boardId,
    pageSize = DEFAULT_PAGE_SIZE
  }: {
    client: ApolloClient<object>
    boardId: string
    pageSize?: number
  }) {
    this.client = client
    this.boardId = boardId
    this.pageSize = pageSize
  }

  getSnapshot() {
    const oldestCreated = this.appearances.length
      ? this.appearances[this.appearances.length - 1].created
      : null
    const newestCreated = this.appearances.length ? this.appearances[0].created : null
    return {
      loading: this.loading,
      error: this.error,
      exhausted: this.exhausted,
      nextCursor: this.nextCursor,
      oldestCreated,
      newestCreated,
      hasFetchedHead: this.hasFetchedHead
    }
  }

  getNextCursor() {
    return this.nextCursor
  }

  isExhausted() {
    return this.exhausted
  }

  queryRange(range: TimeRange) {
    if (this.appearances.length === 0) return []
    const start = lowerBoundDescByCreated(this.appearances, range.max)
    const end = lowerBoundDescByCreated(this.appearances, range.min - 1)
    if (start >= end) return []
    return this.appearances.slice(start, end)
  }

  isRangeCovered(range: TimeRange) {
    if (!this.hasFetchedHead) return false
    const oldestCreated = this.appearances.length
      ? this.appearances[this.appearances.length - 1].created
      : Number.POSITIVE_INFINITY
    return oldestCreated <= range.min || this.exhausted
  }

  async fetchNextPage() {
    if (this.loading || this.exhausted) {
      return {
        rows: [] as MessageAppearanceRow[],
        nextCursor: this.nextCursor
      }
    }

    this.loading = true
    this.error = null
    try {
      const result = await this.client.query<
        ListBoardMessageAppearancesForArrayHistory,
        ListBoardMessageAppearancesForArrayHistoryVariables
      >({
        query: LIST_BOARD_MESSAGE_APPEARANCES_QUERY,
        variables: {
          id: this.boardId,
          cursor: this.nextCursor,
          limit: this.pageSize
        },
        fetchPolicy: 'cache-first'
      })

      const payload = result.data?.listBoardMessageAppearancesV2
      const rows = payload?.messageAppearances || []
      const seen = new Set(this.appearances.map(row => row.id))
      const additions = rows.filter(row => !seen.has(row.id))
      this.appearances = [...this.appearances, ...additions]
      this.hasFetchedHead = true
      this.nextCursor = payload?.nextCursor || null
      this.exhausted = this.nextCursor == null
      return {
        rows: additions,
        nextCursor: this.nextCursor
      }
    } catch {
      this.error = `Unable to load board history for ${this.boardId}`
      throw new Error(this.error)
    } finally {
      this.loading = false
    }
  }

  async ensureRange(range: TimeRange): Promise<EnsureRangeStatus> {
    if (this.isRangeCovered(range)) return 'complete'

    let paginationStalled = false
    while (!this.isRangeCovered(range) && !this.exhausted) {
      const previousCursor = this.nextCursor
      const { rows } = await this.fetchNextPage()
      if (rows.length === 0 && this.exhausted) {
        break
      }
      if (rows.length === 0 && this.nextCursor === previousCursor) {
        paginationStalled = true
        break
      }

      if (rows.length > 0) {
        const oldestCreatedOnPage = rows[rows.length - 1].created
        if (oldestCreatedOnPage <= range.min) {
          break
        }
      }
    }

    if (this.exhausted) {
      return 'exhausted'
    }
    if (this.isRangeCovered(range)) {
      return 'complete'
    }
    return paginationStalled ? 'partial' : 'partial'
  }

  trimToRanges(ranges: TimeRange[], bufferMs: number) {
    if (ranges.length === 0) {
      this.appearances = []
      this.hasFetchedHead = false
      this.exhausted = false
      this.nextCursor = null
      return
    }

    const buffered = expandRanges(ranges, bufferMs)
    const previousLength = this.appearances.length
    this.appearances = this.appearances.filter(row =>
      buffered.some(range => row.created >= range.min && row.created <= range.max)
    )
    const removedRows = this.appearances.length < previousLength
    if (removedRows) {
      this.exhausted = false
      this.nextCursor = null
    }
    if (this.appearances.length === 0) {
      this.hasFetchedHead = false
      return
    }
    this.hasFetchedHead = true
  }
}
